import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Option } from '@digilize/shared/definitions/src';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, of } from 'rxjs';

import { Vacancy } from '../../models/vacancy.model';
import { VacancyMockService } from '../../services/vacancy-mock.service';
import { VacancyDetailData } from '../vacancy-detail/vacancy-detail.component';

@Component({
  selector: 'bwc-vacancy-search',
  templateUrl: './vacancy-search.component.html',
  styleUrls: ['./vacancy-search.component.scss'],
})
export class VacancySearchComponent implements OnInit {
  vacancies: Vacancy[] = [];
  visibleCount = 30;
  likedIds: number[] = [];
  hasSearched = false;

  searchFunction = '';
  searchLocation = '';
  searchRadius = '10';
  selectedVakgebied = '';
  selectedOpleidingsniveau = '';
  selectedAantalUren = '';
  selectedPlaatsingsdatum = '';

  radiusControl = new FormControl('10');
  vakgebiedControl = new FormControl('');
  opleidingsniveauControl = new FormControl('');
  aantalUrenControl = new FormControl('');
  plaatsingsdatumControl = new FormControl('');

  radiusOptions: Option[] = [];
  vakgebiedOptions: Option[] = [];
  opleidingsniveauOptions: Option[] = [];
  aantalUrenOptions: Option[] = [];
  plaatsingsdatumOptions: Option[] = [];

  private visibleVacancies$ = new BehaviorSubject<Vacancy[]>([]);

  detailData: VacancyDetailData | null = null;

  constructor(private vacancyService: VacancyMockService, private translateService: TranslateService) {}

  ngOnInit(): void {
    this.vacancyService.getVacancies().subscribe((v) => (this.vacancies = v));
    this.likedIds = this.vacancyService.getSavedIds();

    this.radiusOptions = [
      { text: this.translateService.instant('vacancies.search.distance_options.5'), val: '5' },
      { text: this.translateService.instant('vacancies.search.distance_options.10'), val: '10' },
      { text: this.translateService.instant('vacancies.search.distance_options.25'), val: '25' },
      { text: this.translateService.instant('vacancies.search.distance_options.50'), val: '50' },
      { text: this.translateService.instant('vacancies.search.distance_options.100'), val: '100' },
    ];

    this.vakgebiedOptions = [
      { text: this.translateService.instant('shared.all_fields'), val: '' },
      { text: this.translateService.instant('vacancies.search.filter_tech'), val: 'techniek' },
      { text: this.translateService.instant('vacancies.search.filter_it'), val: 'it' },
      { text: this.translateService.instant('vacancies.search.filter_economics'), val: 'economie' },
      { text: this.translateService.instant('vacancies.search.filter_marketing_comm'), val: 'marketing' },
      { text: this.translateService.instant('vacancies.search.filter_hr_personnel'), val: 'hr' },
      { text: this.translateService.instant('vacancies.search.filter_care_welfare'), val: 'zorg' },
    ];

    this.opleidingsniveauOptions = [
      { text: this.translateService.instant('shared.all_levels'), val: '' },
      { text: this.translateService.instant('shared.filters.mbo'), val: 'mbo' },
      { text: this.translateService.instant('shared.filters.hbo'), val: 'hbo' },
      { text: this.translateService.instant('shared.filters.wo'), val: 'wo' },
    ];

    this.aantalUrenOptions = [
      { text: this.translateService.instant('vacancies.search.filter_all_hours'), val: '' },
      { text: this.translateService.instant('vacancies.search.filter_fulltime'), val: 'fulltime' },
      { text: this.translateService.instant('vacancies.search.filter_parttime'), val: 'parttime' },
      { text: this.translateService.instant('vacancies.search.filter_32h'), val: '32' },
      { text: this.translateService.instant('vacancies.search.filter_24h'), val: '24' },
    ];

    this.plaatsingsdatumOptions = [
      { text: this.translateService.instant('vacancies.search.filter_all_dates'), val: '' },
      { text: this.translateService.instant('vacancies.search.filter_today'), val: 'vandaag' },
      { text: this.translateService.instant('vacancies.search.filter_last_week'), val: 'week' },
      { text: this.translateService.instant('vacancies.search.filter_last_month'), val: 'maand' },
    ];
  }

  get visibleVacancies(): Vacancy[] {
    const visible = this.vacancies.slice(0, this.visibleCount);
    this.visibleVacancies$.next(visible);
    return visible;
  }

  get hasMore(): boolean {
    return this.visibleCount < this.vacancies.length;
  }

  search(): void {
    this.hasSearched = true;
  }

  loadMore(): void {
    this.visibleCount = Math.min(this.visibleCount + 30, this.vacancies.length);
  }

  openDetail(vacancy: Vacancy): void {
    const visible = this.vacancies.slice(0, this.visibleCount);
    this.visibleVacancies$.next(visible);
    const index = visible.findIndex((v) => v.id === vacancy.id);
    if (index < 0) return;
    this.detailData = {
      vacancies$: this.visibleVacancies$.asObservable(),
      initialIndex: index,
      hasMore$: of(false),
    };
  }

  closeDetail(): void {
    this.detailData = null;
  }

  isLiked(id: number): boolean {
    return this.likedIds.includes(id);
  }

  toggleLike(vacancy: Vacancy): void {
    const saved = this.vacancyService.toggleSaveVacancy(vacancy);
    if (saved) {
      this.likedIds = [...this.likedIds, vacancy.id];
    } else {
      this.likedIds = this.likedIds.filter((i) => i !== vacancy.id);
    }
  }

  onRadiusSelected(option: Option): void {
    this.searchRadius = option.val;
  }

  onVakgebiedSelected(option: Option): void {
    this.selectedVakgebied = option.val;
  }

  onOpleidingsniveauSelected(option: Option): void {
    this.selectedOpleidingsniveau = option.val;
  }

  onAantalUrenSelected(option: Option): void {
    this.selectedAantalUren = option.val;
  }

  onPlaatsingsdatumSelected(option: Option): void {
    this.selectedPlaatsingsdatum = option.val;
  }
}
